/**
 * A short list of named options, as a row of buttons with one of them pressed.
 *
 * The second control kind, and the first that is not a magnitude. A HUD skin is not more or less of
 * anything: there is no middle between a diegetic frame and a conventional one, and a slider
 * between them would be a control promising a third thing that does not exist. Buttons say the
 * options are the whole list and that picking one is picking, not tuning.
 *
 * The row itself is [optionRow](../../controls/optionRow.ts), which owns the `radiogroup`, the
 * `aria-checked` and the pressed class. It was born here and moved out the day the seed picker
 * wrote it a second time; what stays behind is everything that makes the row a settings control:
 * the contract kinds, the stored-value coercion, and the classes the settings stylesheet draws.
 *
 * Only `onCommit` fires. The split between change and commit exists because a drag passes through
 * forty values on the way to one decision, and a press passes through none: it is the decision. So
 * a press applies and writes in the same call, and this control never calls `onChange` at all.
 *
 * `choiceApply` is the other half of the file, on the same terms `rangeSlider.ts` states for its
 * own: the one place that knows this kind produces a string is the place that owns the kind.
 */

import { createOptionRow } from '@/ui/controls/optionRow';
import { el } from '@/ui/dom';
import type {
  SettingControl,
  SettingControlDefinition,
  SettingControlHandle,
  SettingControlOptions,
  SettingValue,
} from '@/ui/settings/settingContract';

/**
 * A knob's `apply`, narrowed to what this control produces.
 *
 * Anything that is not a string never reaches the wrapped function, which `coerce` below already
 * guarantees. Whether the string is an option this build knows is the knob's own business: a value
 * from a later build is a name this one has never heard of, and the module that owns the quantity
 * is the only thing that can say so.
 */
export function choiceApply(apply: (value: string) => void): (value: SettingValue) => void {
  return (value: SettingValue): void => {
    if (typeof value === 'string') apply(value);
  };
}

export const CHOICE_BUTTONS: SettingControlDefinition = {
  kind: 'choice',

  /**
   * A stored id this build offers, or nothing.
   *
   * Out of range has no meaning here, which is the one way this differs from a slider: there is
   * nothing to pull an unknown name back towards, so it falls through to the knob's default. That
   * is the same three answers `settingValues.ts` states, with the middle one empty.
   */
  coerce(control: SettingControl, raw: unknown): SettingValue | null {
    if (control.kind !== 'choice') return null;
    if (typeof raw !== 'string') return null;
    return control.options.some((option) => option.value === raw) ? raw : null;
  },

  create(options: SettingControlOptions): SettingControlHandle {
    const control = options.definition.control;
    // The registry resolved this control by its own kind, so nothing else can arrive here. An
    // empty handle rather than a throw: a settings screen missing one row beats one that is blank.
    if (control.kind !== 'choice') return { root: el('div', 'settings-control'), dispose: noop };

    const row = createOptionRow({
      label: options.definition.label,
      choices: control.options,
      // Coercion already guaranteed a string; the guard is only here because the contract's value
      // is as wide as every control kind, and an empty name opens the row with nothing pressed.
      initial: typeof options.value === 'string' ? options.value : '',
      groupClass: 'settings-control settings-choice',
      optionClass: 'settings-choiceOption',
      onPick: options.onCommit,
    });

    return {
      root: row.root,
      dispose: (): void => {
        row.dispose();
      },
    };
  },
};

/** What the unreachable branch in `create` hands back, so a missing row disposes like any other. */
function noop(): void {
  return undefined;
}
